import { useOutletContext } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  ResponsiveContainer,
  ReferenceLine,
  Cell
} from "recharts";

// ===== MyAnalytics Data =====
const monthlyData = [
  { name: "Jan", amount: 150000 },
  { name: "Feb", amount: 75000 },
  { name: "Mar", amount: 180000 },
  { name: "Apr", amount: 50000 },
  { name: "May", amount: 250000 },
  { name: "Jun", amount: 165000 },
  { name: "Jul", amount: 135000 },
  { name: "Aug", amount: 175000 },
  { name: "Sep", amount: 110000 },
  { name: "Oct", amount: 75000 },
  { name: "Nov", amount: 145000 },
  { name: "Dec", amount: 165000 },
];

const lawFirmData = [
  { name: "Harrington Law Partners", value: 500000, color: "hsl(230, 80%, 30%)" },
  { name: "Sagebridge Legal Group", value: 480000, color: "hsl(38, 92%, 50%)" },
  { name: "Crawford, Hale & Benton", value: 320000, color: "hsl(188, 78%, 51%)" },
  { name: "Redwood & Cole Attorneys at Law", value: 450000, color: "hsl(142, 71%, 40%)" },
  { name: "Whitestone & Parker LLP", value: 150000, color: "hsl(220, 60%, 25%)" },
];

// ===== Temwise Analytics Data =====
const expenditureData = [
  { name: "Jan", withoutTemwise: 0, withTemwise: 0 },
  { name: "Feb", withoutTemwise: 250000, withTemwise: 50000 },
  { name: "Mar", withoutTemwise: 280000, withTemwise: 125000 },
  { name: "Apr", withoutTemwise: 310000, withTemwise: 140000 },
  { name: "May", withoutTemwise: 315000, withTemwise: 145000 },
  { name: "Jun", withoutTemwise: 320000, withTemwise: 155000 },
  { name: "Jul", withoutTemwise: 330000, withTemwise: 180000 },
  { name: "Aug", withoutTemwise: 340000, withTemwise: 200000 },
];

const rfpsPerMonthData = [
  { name: "Jan", rfps: 6 },
  { name: "Feb", rfps: 6 },
  { name: "Mar", rfps: 9 },
  { name: "Apr", rfps: 7 },
  { name: "May", rfps: 11 },
  { name: "Jun", rfps: 8 },
  { name: "Jul", rfps: 9 },
];

// ===== Legal Industry Intel Data =====
const hourlyRateData = [
  { role: "Paralegal", yourFirm: 200, market: 250 },
  { role: "Trainee", yourFirm: 550, market: 800 },
  { role: "Associate", yourFirm: 900, market: 1000 },
  { role: "Senior Associate", yourFirm: 1250, market: 1400 },
  { role: "Partner", yourFirm: 1500, market: 2000 },
];

const expenditureDistributionData = [
  { role: "Paralegal", yourFirm: 10, market: 15 },
  { role: "Trainee", yourFirm: 20, market: 25 },
  { role: "Associate", yourFirm: 30, market: 30 },
  { role: "Senior Associate", yourFirm: 30, market: 25 },
  { role: "Partner", yourFirm: 10, market: 15 },
];

const chartConfig = {
  amount: { theme: { light: "hsl(38, 92%, 50%)", dark: "hsl(38, 92%, 50%)" } },
};

// Section Header Component
const SectionHeader = ({ title, subtitle }: { title: string; subtitle: string }) => (
  <div className="mb-6">
    <h2 className="text-xl font-semibold tracking-tight text-foreground">{title}</h2>
    <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{subtitle}</p>
  </div>
);

// KPI Card Component
const KPICard = ({ 
  title, 
  value, 
  subtitle,
  valueColor = "text-green-600" 
}: { 
  title: string; 
  value: string; 
  subtitle?: string;
  valueColor?: string;
}) => (
  <Card className="border border-border bg-card shadow-sm">
    <CardContent className="p-5 flex flex-col justify-center h-full min-h-[120px]">
      <h3 className="text-sm font-medium text-muted-foreground leading-snug mb-2">{title}</h3>
      <p className={`text-3xl font-bold ${valueColor}`}>{value}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </CardContent>
  </Card>
);

const Knowledge = () => {
  const context = useOutletContext();

  if (context === "title-only") {
    return "Knowledge";
  }

  return (
    <div className="animate-fade-in py-6 px-4 space-y-8">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Knowledge</h1>
        <p className="text-muted-foreground mt-1">
          See how much your legal operations team has saved.
        </p>
      </div>

      {/* ===== Section 1: MyAnalytics ===== */}
      <section>
        <SectionHeader 
          title="MyAnalytics" 
          subtitle="Gain insights into how you are spending on external counsel."
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Monthly Legal Expenditure */}
          <Card className="border border-border bg-card shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-sm font-medium text-foreground mb-4">
                Monthly Legal Expenditure Against Budget
              </h3>
              <div className="h-[180px]">
                <ChartContainer config={chartConfig} className="h-full w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={{ stroke: "hsl(var(--border))" }} tickLine={false} />
                      <YAxis tickFormatter={(v) => `$${v / 1000}k`} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <ReferenceLine y={150000} stroke="hsl(200, 80%, 55%)" strokeWidth={2} />
                      <Line type="monotone" dataKey="amount" stroke="hsl(38, 92%, 50%)" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>
            </CardContent>
          </Card>

          {/* Law Firm Distribution */}
          <Card className="border border-border bg-card shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-sm font-medium text-foreground mb-4">
                Law Firm Expenditure Distribution
              </h3>
              <div className="h-[180px]">
                <ChartContainer config={chartConfig} className="h-full w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={lawFirmData} layout="vertical" margin={{ top: 0, right: 10, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
                      <XAxis type="number" tickFormatter={(v) => `${v / 1000}k`} tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} axisLine={{ stroke: "hsl(var(--border))" }} tickLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 9, fill: "hsl(var(--muted-foreground))" }} width={110} axisLine={false} tickLine={false} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {lawFirmData.map((entry, index) => (
                          <Cell key={index} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>
            </CardContent>
          </Card>

          {/* KPI Cards */}
          <div className="flex flex-col gap-4">
            <KPICard 
              title="Monthly Expenditure Against Budget" 
              value="-$643,194" 
              subtitle="-35.73%"
              valueColor="text-red-500"
            />
            <KPICard 
              title="Region with Highest Expenditure" 
              value="North America"
            />
          </div>
        </div>
      </section>

      <Separator className="my-8" />

      {/* ===== Section 2: Temwise Analytics ===== */}
      <section>
        <SectionHeader 
          title="Temwise Analytics Dashboard" 
          subtitle="See how your organisation is utilising Temwise."
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Expenditure with Temwise */}
          <Card className="border border-border bg-card shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-sm font-medium text-foreground mb-3">Expenditure with Temwise</h3>
              <div className="flex items-center gap-4 mb-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-0.5 bg-[hsl(200,80%,55%)]" />
                  <span className="text-muted-foreground">Without Temwise</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-0.5 bg-[hsl(38,92%,50%)]" />
                  <span className="text-muted-foreground">With Temwise</span>
                </div>
              </div>
              <div className="h-[160px]">
                <ChartContainer config={chartConfig} className="h-full w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={expenditureData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={{ stroke: "hsl(var(--border))" }} tickLine={false} />
                      <YAxis tickFormatter={(v) => `$${v / 1000}k`} tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Line type="monotone" dataKey="withoutTemwise" stroke="hsl(200, 80%, 55%)" strokeWidth={2} dot={false} name="Without Temwise" />
                      <Line type="monotone" dataKey="withTemwise" stroke="hsl(38, 92%, 50%)" strokeWidth={2} dot={false} name="With Temwise" />
                    </LineChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>
            </CardContent>
          </Card>

          {/* Instructions per Month */}
          <Card className="border border-border bg-card shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-sm font-medium text-foreground mb-4">Number of Instructions per Month</h3>
              <div className="h-[180px]">
                <ChartContainer config={chartConfig} className="h-full w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={rfpsPerMonthData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={{ stroke: "hsl(var(--border))" }} tickLine={false} />
                      <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="rfps" fill="hsl(200, 80%, 55%)" radius={[4, 4, 0, 0]} name="RFPs" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>
            </CardContent>
          </Card>

          {/* KPI Cards */}
          <div className="flex flex-col gap-4">
            <KPICard title="Amount Saved Using Temwise YTD" value="$4.85m" />
            <KPICard title="Avg Reverse Auction Savings" value="12.3%" />
          </div>
        </div>
      </section>

      <Separator className="my-8" />

      {/* ===== Section 3: Legal Industry Intel ===== */}
      <section>
        <SectionHeader 
          title="Legal Industry Intel" 
          subtitle="Leverage market intel to assess your in-house teams spending patterns against the wider market."
        />
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Hourly Rate */}
          <Card className="border border-border bg-card shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-sm font-medium text-foreground mb-3">Average Market Hourly Rate</h3>
              <div className="flex items-center gap-4 mb-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-[hsl(200,80%,55%)]" />
                  <span className="text-muted-foreground">Your Firm (USD)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-[hsl(38,92%,50%)]" />
                  <span className="text-muted-foreground">Market Avg (USD)</span>
                </div>
              </div>
              <div className="h-[160px]">
                <ChartContainer config={chartConfig} className="h-full w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={hourlyRateData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="role" tick={{ fontSize: 8, fill: "hsl(var(--muted-foreground))" }} axisLine={{ stroke: "hsl(var(--border))" }} tickLine={false} interval={0} />
                      <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="yourFirm" fill="hsl(200, 80%, 55%)" radius={[4, 4, 0, 0]} name="Your Firm" />
                      <Bar dataKey="market" fill="hsl(38, 92%, 50%)" radius={[4, 4, 0, 0]} name="Market Average" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>
            </CardContent>
          </Card>

          {/* Expenditure Distribution */}
          <Card className="border border-border bg-card shadow-sm">
            <CardContent className="p-5">
              <h3 className="text-sm font-medium text-foreground mb-3">Distribution of Legal Expenditure</h3>
              <div className="flex items-center gap-4 mb-3 text-xs">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-[hsl(200,80%,55%)]" />
                  <span className="text-muted-foreground">Your Firm %</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-sm bg-[hsl(38,92%,50%)]" />
                  <span className="text-muted-foreground">Market %</span>
                </div>
              </div>
              <div className="h-[160px]">
                <ChartContainer config={chartConfig} className="h-full w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={expenditureDistributionData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                      <XAxis dataKey="role" tick={{ fontSize: 8, fill: "hsl(var(--muted-foreground))" }} axisLine={{ stroke: "hsl(var(--border))" }} tickLine={false} interval={0} />
                      <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Bar dataKey="yourFirm" stackId="a" fill="hsl(200, 80%, 55%)" name="Your Firm %" />
                      <Bar dataKey="market" stackId="a" fill="hsl(38, 92%, 50%)" radius={[4, 4, 0, 0]} name="Market %" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartContainer>
              </div>
            </CardContent>
          </Card>

          {/* KPI Cards */}
          <div className="flex flex-col gap-4">
            <KPICard 
              title="Blended Hourly Rate Market Comparison" 
              value="6%" 
              subtitle="Lower than market"
            />
            <KPICard 
              title="Legal Industry Expenditure YTD Compared to Last Year" 
              value="10%"
            />
          </div>
        </div>
      </section>
    </div>
  );
};

export default Knowledge;